import type { ReactNode } from 'react';
import { MicIcon, UploadIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Een uitgeschakelde knop krijgt geen muisgebeurtenissen, dus de tooltip hangt
 * aan een omhulsel eromheen. `tabIndex` erbij, anders is de reden alleen met de
 * muis te vinden.
 */
function MetReden({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex" tabIndex={0}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>Je hebt hier niet de juiste rechten voor</TooltipContent>
    </Tooltip>
  );
}

/**
 * De twee ingangen naar een nieuw gesprek: opnemen, of een bestand kiezen.
 *
 * Opnemen gaat overal door de browser. Eerst opende het op een telefoon de
 * opname-app van het toestel, omdat die doorloopt met het scherm uit. Dat viel
 * af: zo'n app is er niet altijd, en dan biedt het toestel alleen zijn camera en
 * zijn schijf aan. Op een toestel zonder recorder is dat een doodlopende weg.
 *
 * Zonder buddyvinkje staan ze er nog steeds, uitgeschakeld, met de reden
 * eronder. Ze weglaten was de andere optie; dan mist een leeg scherm elke
 * aanwijzing dat er iets te halen valt en waarom het niet lukt.
 */
export function OpnameKnoppen({
  className,
  magAanleveren,
  onVenster,
  onOpnemen,
}: {
  className?: string;
  /** Het buddyvinkje. Zonder dat weigert de server het toch. */
  magAanleveren: boolean;
  /** Het gewone venster, leeg. Daar zit het slepen en het kiezen al in. */
  onVenster: () => void;
  /** Hetzelfde venster, met de microfoon in het opnamevak. */
  onOpnemen: () => void;
}) {
  const omhul = (knop: ReactNode) => (magAanleveren ? knop : <MetReden>{knop}</MetReden>);

  return (
    <TooltipProvider>
      <div className={cn('flex items-center gap-2', className)}>
        {omhul(
          <Button disabled={!magAanleveren} onClick={onVenster} variant="outline">
            <UploadIcon data-icon="inline-start" />
            Bestand kiezen
          </Button>,
        )}
        {omhul(
          <Button disabled={!magAanleveren} onClick={onOpnemen}>
            <MicIcon data-icon="inline-start" />
            Opnemen
          </Button>,
        )}
      </div>
    </TooltipProvider>
  );
}
